// V3.3 email-context: thin fetch wrappers for the email-context endpoints the
// attach-UI and the email_message chip use. Backend lives at
// REACT_APP_BACKEND_BASE_ENDPOINT; auth is the Clerk "Backend" JWT, mirroring
// src/utils/messageReliability.js.
//
// Errors come back as { ok: false, status, error } so callers can branch on
// status code without try/catch ladders in the UI.
//
// Reads have two variants: the owner routes (/api/email-threads/...) and the
// shared-chat routes nested under a chat thread id
// (/api/thread/{chatThreadId}/email-threads/...), which is what authorizes a
// non-owner's read. Response shapes are identical; only the path and the auth
// handling differ. Callers pass `sharedChatThreadId` to select the shared
// variant — see SharedChatOption.

const BASE_ENDPOINT = process.env.REACT_APP_BACKEND_BASE_ENDPOINT;

// The shared routes take Authorization as a *required* header — omitting it is
// a 422 from FastAPI validation, not a friendly error — but accept a
// non-credential value, because shared links are opened by people with no
// session. `Bearer null` is the convention the other shared endpoints already
// use (get-shared-messages, get-shared-text).
export const ANONYMOUS_BEARER_TOKEN = "null";

export interface ApiOk<T> {
  ok: true;
  status: number;
  data: T;
}

export interface ApiErr {
  ok: false;
  status: number;
  error: string;
}

export type ApiResult<T> = ApiOk<T> | ApiErr;

interface RequestOptions {
  method?: string;
  body?: unknown;
  token?: string | null;
  signal?: AbortSignal;
  // Send `Bearer null` instead of short-circuiting when there is no session.
  // Only the shared-chat routes accept that; owner routes keep bailing out
  // early so a signed-out user never fires a request that can only 401.
  allowAnonymousToken?: boolean;
}

// Set `sharedChatThreadId` to read through the shared-chat routes nested under
// that chat thread id (the id already in the shared chat page's URL). Selection
// is made from the page context at the call site, never as a fallback after an
// owner-path 404: the owner path 404s legitimately for a non-owner, so falling
// back would double every request and mask real errors.
interface SharedChatOption {
  sharedChatThreadId?: string | null;
}

// An email address as it appears on a participant / recipient list.
export interface EmailAddress {
  email: string;
  name?: string | null;
}

export interface EmailAttachment {
  id: string;
  filename: string;
  content_type: string;
  size_bytes: number;
  sha256: string;
  virus_scan_status: string;
}

export interface EmailMessageDetail {
  id: string;
  to_addresses?: EmailAddress[];
  cc_addresses?: EmailAddress[];
  subject?: string | null;
  sent_at?: string | null;
  received_at?: string | null;
  body_text?: string | null;
  body_html?: string | null;
  spf_pass?: boolean;
  dkim_pass?: boolean;
  dmarc_pass?: boolean;
  attachments?: EmailAttachment[];
}

export interface EmailThreadDetail {
  id: string;
  subject?: string | null;
  participants?: EmailAddress[];
  first_seen_at?: string | null;
  last_message_at?: string | null;
  message_count?: number;
  attachment_count?: number;
  attachment_filenames?: string[];
  messages: EmailMessageDetail[];
}

export interface AttachmentDownload {
  url: string;
  filename: string;
  content_type: string;
}

export interface EmailContextSettings {
  address: string;
  threads_total?: number;
  last_received_at?: string | null;
}

export interface EmailThreadSummary {
  id: string;
  subject?: string | null;
  participants?: EmailAddress[];
  last_message_at?: string | null;
  message_count?: number;
  attachment_count?: number;
  attachment_filenames?: string[];
}

// V3.3 email-attachment: per-attachment and per-thread extraction status, as
// returned by GET /email-context/attachments-status.
export interface EmailAttachmentStatusEntry {
  id: string;
  filename: string;
  status: string; // "pending" | "processing" | "successful" | "failed"
}

export interface EmailThreadAttachmentsStatus {
  email_thread_id: string;
  subject?: string | null;
  status: string; // aggregate: "pending" | "processing" | "successful" | "failed"
  attachments: EmailAttachmentStatusEntry[];
}

export interface EmailAttachmentsStatusResponse {
  processing_complete: boolean;
  email_threads: EmailThreadAttachmentsStatus[];
}

const _request = async <T = unknown>(
  path: string,
  { method = "GET", body, token, signal, allowAnonymousToken = false }: RequestOptions = {},
): Promise<ApiResult<T>> => {
  const authToken = token || (allowAnonymousToken ? ANONYMOUS_BEARER_TOKEN : null);
  if (!authToken) {
    return { ok: false, status: 0, error: "missing_token" };
  }
  try {
    const res = await fetch(`${BASE_ENDPOINT}${path}`, {
      method,
      signal,
      headers: {
        Authorization: `Bearer ${authToken}`,
        ...(body ? { "Content-Type": "application/json" } : {}),
      },
      body: body ? JSON.stringify(body) : undefined,
    });
    if (!res.ok) {
      return { ok: false, status: res.status, error: `http_${res.status}` };
    }
    const data = (await res.json()) as T;
    return { ok: true, status: res.status, data };
  } catch (e: any) {
    if (e?.name === "AbortError") {
      return { ok: false, status: 0, error: "aborted" };
    }
    return { ok: false, status: 0, error: e?.message || "network_error" };
  }
};

export const getEmailContextSettings = (
  token: string | null,
  { signal }: { signal?: AbortSignal } = {},
): Promise<ApiResult<EmailContextSettings>> =>
  _request<EmailContextSettings>("/api/email-context/settings", { token, signal });

export const rotateEmailContextToken = (token: string) =>
  _request("/api/email-context/rotate-token", { method: "POST", token });

export const listEmailThreads = (
  token: string | null,
  { limit = 20, signal }: { limit?: number; signal?: AbortSignal } = {},
): Promise<ApiResult<EmailThreadSummary[]>> =>
  _request<EmailThreadSummary[]>(`/api/email-threads?limit=${encodeURIComponent(limit)}`, { token, signal });

// V3.3 email-context: fetch a single email thread's full conversation for the
// EmailThreadDrawer. The owner route is owner-checked — 404 when the thread is
// not owned. The shared-chat route grants access per-chat: only email threads
// actually attached to that chat are readable through it, and quarantined or
// deleted threads are blocked there even while still chipped into the chat (so
// the same email can render for the owner and 404 for a shared viewer — that is
// intended). Legacy `email_message` chips resolve on both routes, so no
// special-casing by chip type is needed.
export const getEmailThreadDetail = (
  token: string | null,
  emailThreadId: string,
  { signal, sharedChatThreadId }: { signal?: AbortSignal } & SharedChatOption = {},
): Promise<ApiResult<EmailThreadDetail>> => {
  const emailPath = `email-threads/${encodeURIComponent(emailThreadId)}`;
  const path = sharedChatThreadId
    ? `/api/thread/${encodeURIComponent(sharedChatThreadId)}/${emailPath}`
    : `/api/${emailPath}`;
  return _request<EmailThreadDetail>(path, {
    token,
    signal,
    allowAnonymousToken: Boolean(sharedChatThreadId),
  });
};

// V3.3 email-context: resolve a presigned download URL for an attachment.
// Returns 403 until the attachment is virus-scan clean, 500 if presigning fails
// (retryable). The URL expires in 15 minutes, so this is called on click rather
// than pre-fetched at render.
export const downloadEmailAttachment = (
  token: string | null,
  attachmentId: string,
  { sharedChatThreadId }: SharedChatOption = {},
): Promise<ApiResult<AttachmentDownload>> => {
  const attachmentPath = `email-attachments/${encodeURIComponent(attachmentId)}/download`;
  const path = sharedChatThreadId
    ? `/api/thread/${encodeURIComponent(sharedChatThreadId)}/${attachmentPath}`
    : `/api/${attachmentPath}`;
  return _request<AttachmentDownload>(path, {
    token,
    allowAnonymousToken: Boolean(sharedChatThreadId),
  });
};

// V3.3 email-attachment: poll extraction status for one or more attached
// threads. Repeats `email_thread_ids` once per id (FastAPI list-query
// convention) rather than comma-joining — a single joined value would be
// parsed as one opaque id, not a list, by the backend.
export const getEmailAttachmentsStatus = (
  token: string | null,
  emailThreadIds: string[],
  { signal }: { signal?: AbortSignal } = {},
): Promise<ApiResult<EmailAttachmentsStatusResponse>> => {
  const query = emailThreadIds.map((id) => `email_thread_ids=${encodeURIComponent(id)}`).join("&");
  return _request<EmailAttachmentsStatusResponse>(`/api/email-context/attachments-status?${query}`, {
    token,
    signal,
  });
};
